/**
 * Serviço base de API para comunicação com o backend Django
 */

export const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.sideb.com.br/api';

/**
 * A sessão viaja num cookie httpOnly que o navegador anexa sozinho — o
 * JavaScript não lê nem escreve essa credencial, que é justamente o ponto.
 * Toda requisição precisa de `credentials: 'include'`: o app e a API estão em
 * hosts diferentes, e sem isso o navegador não manda o cookie.
 */
export const COM_CREDENCIAIS = { credentials: 'include' };

/**
 * Mensagem de erro que a tela mostra, a partir do corpo que o backend mandou.
 *
 * O DRF devolve erro de validação por CAMPO — `{"student_serial": ["Aluno com
 * este student serial já existe."]}` — e não em `error` nem em `detail`. A
 * montagem anterior olhava só essas duas chaves, então a secretaria via
 * "Erro na requisição: 400" e nada mais: o motivo vinha na resposta e era
 * jogado fora. Foi o que aconteceu ao transferir um aluno para outra escola —
 * a matrícula já existia em outro cadastro, e a tela não disse isso.
 *
 * Os rótulos existem porque o nome do campo é o da coluna, não o da tela:
 * "student_serial" não ajuda quem está olhando o formulário.
 */
const ROTULOS_DE_CAMPO = {
  student_serial: 'Matrícula',
  student_name: 'Nome do aluno',
  id_class: 'Turma',
  enrollment_date: 'Data de matrícula',
  status: 'Status',
  teacher_serial: 'Matrícula',
  teacher_name: 'Nome',
  cpf: 'CPF',
  email: 'E-mail',
  username: 'Usuário',
  password: 'Senha',
  subject_name: 'Disciplina',
};

// Campos cuja mensagem o backend já escreve como frase inteira, dizendo de
// qual aula se trata. O rótulo na frente só atrapalhava: a professora via
// "class_period: A 1a aula do dia ... já foi registrada" no celular.
const CAMPOS_COM_FRASE_INTEIRA = new Set([
  'non_field_errors',
  'class_period',
  'lesson_date',
]);

export const mensagemDeErro = (corpo, statusHttp) => {
  const generica = `Erro na requisição: ${statusHttp}`;
  if (!corpo || typeof corpo !== 'object') return generica;
  if (corpo.error) return corpo.error;
  if (corpo.detail) return corpo.detail;

  // Erro de validação por campo. `non_field_errors` (e os demais de
  // CAMPOS_COM_FRASE_INTEIRA) não ganha rótulo: já é a frase inteira.
  const partes = [];
  Object.entries(corpo).forEach(([campo, valor]) => {
    const textos = Array.isArray(valor) ? valor : [valor];
    const mensagem = textos.filter((t) => typeof t === 'string').join(' ');
    if (!mensagem) return;
    if (CAMPOS_COM_FRASE_INTEIRA.has(campo)) {
      partes.push(mensagem);
    } else {
      partes.push(`${ROTULOS_DE_CAMPO[campo] || campo}: ${mensagem}`);
    }
  });
  return partes.length ? partes.join(' · ') : generica;
};

/**
 * Pausa do sistema (Configurações › Avisos, "Pausar o sistema"): no horário,
 * o servidor responde 503 com `code: 'sistema_em_pausa'` a tudo. A tela de
 * pausa (PausaDoSistema) escuta este evento para entrar na hora, sem esperar
 * a próxima consulta.
 */
export const EVENTO_SISTEMA_EM_PAUSA = 'sideb:sistema-em-pausa';

const avisarSeEmPausa = (error) => {
  if (error.status !== 503 || error.response?.data?.code !== 'sistema_em_pausa') return;
  window.dispatchEvent(new CustomEvent(EVENTO_SISTEMA_EM_PAUSA, { detail: error.response.data }));
};

/**
 * Headers padrão para requisições autenticadas
 */
const getHeaders = () => ({
  'Content-Type': 'application/json',
});

/**
 * Cidade em que o usuário está trabalhando (combobox do menu lateral).
 *
 * Fica num módulo, e não só no contexto do React, porque quem monta a
 * requisição é o serviço de API — e o recorte por cidade precisa valer para
 * *toda* tela, inclusive as que ninguém lembrou de passar o parâmetro. Só o
 * administrador sem cidade fixa muda esse valor: para os demais perfis o
 * backend já recorta pela cidade do próprio usuário, e mandar o parâmetro
 * seria, na melhor das hipóteses, redundante.
 *
 * Quem escreve aqui é o SelectedCityProvider; ninguém mais deveria.
 */
let cidadeAtiva = null;

export const setCidadeAtiva = (cityId) => {
  cidadeAtiva = cityId === null || cityId === undefined || cityId === '' ? null : cityId;
};

export const getCidadeAtiva = () => cidadeAtiva;

/**
 * Rotas que o recorte por cidade não pode tocar.
 *
 * `/cities/` é a própria lista do seletor: filtrá-la pela cidade escolhida
 * deixaria o administrador preso na cidade em que entrou, sem como voltar
 * para "Todas as cidades".
 */
const SEM_RECORTE_POR_CIDADE = ['/cities/'];

/**
 * `/algo/123/` — busca de um registro específico, por id.
 *
 * O recorte por cidade é um filtro de *lista*: serve para a tela não misturar
 * municípios. Pedir um registro pelo id é outra conversa — quem pode vê-lo já
 * está decidido pelo papel do usuário no backend. Recortar também aqui faria
 * um link salvo para a turma de outra cidade responder "não encontrada".
 */
const ROTA_DE_REGISTRO = /\/\d+\/?$/;

const aceitaRecortePorCidade = (endpoint) =>
  !ROTA_DE_REGISTRO.test(endpoint)
  && !SEM_RECORTE_POR_CIDADE.some(rota => endpoint.startsWith(rota));

/**
 * Acrescenta `city` aos parâmetros quando há cidade escolhida no menu.
 *
 * Quem já passou `city` decide sozinho: a tela pode ter um filtro próprio,
 * mais específico que o do menu. `city: null` é o jeito de dizer "esta busca
 * precisa da rede inteira" — é o caso da lista que monta o próprio seletor de
 * cidades, que recortada deixaria o administrador sem como trocar de
 * município.
 */
export const comCidadeAtiva = (endpoint, params = {}) => {
  if ('city' in params) return params;
  if (cidadeAtiva === null || !aceitaRecortePorCidade(endpoint)) return params;
  return { ...params, city: cidadeAtiva };
};

/**
 * Função genérica para fazer requisições GET
 */
export const apiGet = async (endpoint, params = {}) => {
  const url = new URL(`${API_BASE_URL}${endpoint}`);
  const comCidade = comCidadeAtiva(endpoint, params);
  Object.keys(comCidade).forEach(key => {
    if (comCidade[key] !== undefined && comCidade[key] !== null) {
      url.searchParams.append(key, comCidade[key]);
    }
  });

  const response = await fetch(url.toString(), {
    method: 'GET',
    headers: getHeaders(),
    ...COM_CREDENCIAIS,
  });

  if (!response.ok) {
    // Mesmo tratamento de apiPost/apiPut/apiPatch: o corpo do erro vem junto.
    // Sem isso, a tela só sabia o código HTTP, e a mensagem que o servidor
    // escreveu para a pessoa ler se perdia — "Você ainda não tem turma
    // atribuída" virava "Não foi possível carregar".
    let errorData = {};
    try {
      errorData = await response.json();
    } catch (e) {
      // Resposta sem JSON (502, HTML de proxy): fica só o status.
    }
    const error = new Error(
      errorData.error || errorData.detail || `Erro na requisição: ${response.status}`
    );
    error.status = response.status;
    error.response = { data: errorData };
    avisarSeEmPausa(error);
    throw error;
  }

  return response.json();
};

/**
 * Função para buscar TODOS os dados de um endpoint paginado
 */
export const fetchAllPaginated = async (endpoint, params = {}, options = {}) => {
  // Concorrência: quantas páginas são buscadas ao mesmo tempo.
  // pageSize: quantos registros por requisição (o backend aceita até 1000).
  // Menos idas ao servidor = tela abre muito mais rápido em listas grandes.
  const { concurrency = 6, pageSize = 500 } = options;
  const baseParams = pageSize ? { page_size: pageSize, ...params } : params;

  const first = await apiGet(endpoint, { ...baseParams, page: 1 });

  // Endpoint sem paginação (retorna array puro)
  if (Array.isArray(first)) return first;
  if (!first || !first.results) return [];

  const allData = [...first.results];
  const recebidos = first.results.length;

  // Única página
  if (!first.next || recebidos === 0) return allData;

  // Com `count` sabemos quantas páginas existem e podemos buscá-las em
  // paralelo, em vez de uma de cada vez (era o gargalo: N idas em fila).
  if (typeof first.count === 'number' && first.count > recebidos) {
    const totalPages = Math.ceil(first.count / recebidos);
    const pending = [];
    for (let p = 2; p <= totalPages; p += 1) pending.push(p);

    for (let i = 0; i < pending.length; i += concurrency) {
      const lote = pending.slice(i, i + concurrency);
      // eslint-disable-next-line no-await-in-loop
      const respostas = await Promise.all(
        lote.map((p) => apiGet(endpoint, { ...baseParams, page: p }))
      );
      respostas.forEach((r) => {
        if (r && r.results) allData.push(...r.results);
      });
    }
    return allData;
  }

  // Sem `count` confiável: mantém o comportamento sequencial (segue o `next`).
  let page = 2;
  let hasMore = true;
  while (hasMore) {
    // eslint-disable-next-line no-await-in-loop
    const response = await apiGet(endpoint, { ...baseParams, page });
    if (response && response.results && response.results.length > 0) {
      allData.push(...response.results);
      hasMore = Boolean(response.next);
      page += 1;
    } else {
      hasMore = false;
    }
  }

  return allData;
};

/**
 * Função genérica para fazer requisições POST
 */
export const apiPost = async (endpoint, data) => {
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    method: 'POST',
    headers: getHeaders(),
    ...COM_CREDENCIAIS,
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    let errorData = {};
    try {
      errorData = await response.json();
    } catch (e) {
      // Se não conseguir parsear o JSON, ignora
    }
    const error = new Error(mensagemDeErro(errorData, response.status));
    error.status = response.status;
    error.response = { data: errorData };
    avisarSeEmPausa(error);
    throw error;
  }

  return response.json();
};

/**
 * Função genérica para fazer requisições PUT
 */
export const apiPut = async (endpoint, data) => {
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    method: 'PUT',
    headers: getHeaders(),
    ...COM_CREDENCIAIS,
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    let errorData = {};
    try {
      errorData = await response.json();
    } catch (e) {
      // Se não conseguir parsear o JSON, ignora
    }
    const error = new Error(mensagemDeErro(errorData, response.status));
    error.status = response.status;
    error.response = { data: errorData };
    avisarSeEmPausa(error);
    throw error;
  }

  return response.json();
};

/**
 * Função genérica para fazer requisições PATCH
 */
export const apiPatch = async (endpoint, data) => {
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    method: 'PATCH',
    headers: getHeaders(),
    ...COM_CREDENCIAIS,
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    let errorData = {};
    try {
      errorData = await response.json();
    } catch (e) {
      // Se não conseguir parsear o JSON, ignora
    }
    const error = new Error(mensagemDeErro(errorData, response.status));
    error.status = response.status;
    error.response = { data: errorData };
    avisarSeEmPausa(error);
    throw error;
  }

  return response.json();
};

/**
 * Função genérica para fazer requisições DELETE
 */
export const apiDelete = async (endpoint) => {
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    method: 'DELETE',
    headers: getHeaders(),
    ...COM_CREDENCIAIS,
  });

  if (!response.ok) {
    let errorData = {};
    try {
      errorData = await response.json();
    } catch (e) {
      // Se não conseguir parsear o JSON, ignora
    }
    const error = new Error(mensagemDeErro(errorData, response.status));
    error.status = response.status;
    error.response = { data: errorData };
    avisarSeEmPausa(error);
    throw error;
  }

  return response.status === 204 ? null : response.json();
};

/**
 * Função para upload de arquivos (FormData)
 * Não define Content-Type para permitir que o browser defina automaticamente com boundary
 */
export const apiUpload = async (endpoint, formData, onProgress = null) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.open('POST', `${API_BASE_URL}${endpoint}`);
    // O equivalente de credentials:'include' no XHR.
    xhr.withCredentials = true;

    // Callback de progresso
    if (onProgress) {
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const percentComplete = Math.round((event.loaded / event.total) * 100);
          onProgress(percentComplete);
        }
      };
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const response = JSON.parse(xhr.responseText);
          resolve(response);
        } catch (e) {
          resolve({ success: true, message: 'Upload realizado com sucesso' });
        }
      } else {
        try {
          const errorResponse = JSON.parse(xhr.responseText);
          const error = new Error(errorResponse.error || `Erro na requisição: ${xhr.status}`);
          error.status = xhr.status;
          error.response = errorResponse;
          reject(error);
        } catch (e) {
          const error = new Error(`Erro na requisição: ${xhr.status}`);
          error.status = xhr.status;
          reject(error);
        }
      }
    };

    xhr.onerror = () => {
      reject(new Error('Erro de conexão'));
    };
    xhr.send(formData);
  });
};

/**
 * Função simplificada para upload usando fetch (sem progresso)
 */
export const apiUploadSimple = async (endpoint, formData) => {
  // Sem Content-Type: o navegador monta o boundary do multipart sozinho.
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    method: 'POST',
    body: formData,
    ...COM_CREDENCIAIS,
  });

  const data = await response.json();

  if (!response.ok) {
    const error = new Error(data.error || `Erro na requisição: ${response.status}`);
    error.status = response.status;
    error.response = data;
    throw error;
  }

  return data;
};

/**
 * Busca lista leve de escolas (apenas id e nome) - sem paginação
 * Ideal para dropdowns e filtros
 *
 * @param {number|null} cityId - Filtra escolas por esta cidade. Sem ele vale a
 *   cidade escolhida no menu lateral (ver `comCidadeAtiva`).
 * @param {string} search - Termo opcional de busca
 * @param {boolean} [todasAsCidades] - Ignora o recorte do menu e traz a rede
 *   inteira. Só faz sentido para quem precisa enxergar além do município
 *   escolhido — hoje, o próprio seletor de cidades.
 */
export const fetchSchoolsSimple = async (cityId = null, search = '', todasAsCidades = false) => {
  const params = {};
  if (search) params.search = search;
  if (cityId) params.city = cityId;
  else if (todasAsCidades) params.city = null;
  return apiGet('/schools/simple/', params);
};

export default {
  get: apiGet,
  comCidadeAtiva,
  setCidadeAtiva,
  getCidadeAtiva,
  post: apiPost,
  put: apiPut,
  delete: apiDelete,
  upload: apiUpload,
  uploadSimple: apiUploadSimple,
  fetchAllPaginated,
  fetchSchoolsSimple,
};
